import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Tab,
  Tabs,
  Badge,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { assignedStyles } from './Assigned.styles';
import RefreshIcon from './Images/refresh.svg';
import AddExperienceIcon from './Images/add-experience-plus.svg';
import EmptyCardImage from './Images/empty-card.svg';
import {
  formatLastUpdated,
  toOptionalNumber,
  toRequiredDate,
  toRequiredNumber,
} from '../../../services/Utilities';
import {
  AssignedExperienceFilter,
  AssignedExperienceStatus,
  AssignedService
} from './AssignedService';
import { EndedExperienceNotificationService } from './EndedExperienceNotificationService';
import {
  insertExperienceByStartDate,
  mapSingleExperienceToCard,
  useAssignedRealtime
} from './AssignedRealtime';
import { ExperienceCard } from '../../../components/Experiences/Cards/ExperienceCard/ExperienceCard';
import { StandardsProps } from '../../../components/Experiences/Standards/Standards';
import type { StandardWithBreakouts } from '../../../components/Experiences/Standards/StandardsService';
import {
  ALL_OPTION_ID,
  ALL_OPTION_NUMBER_ID,
  SelectComponent,
  SelectOption
} from '../../../components/core/SelectComponent/SelectComponent';
import { SummaryHeader } from '../../../components/core/SummaryHeader/SummaryHeader';
import { LoadMoreButton } from '../../../components/core/LoadMoreButton/LoadMoreButton';

type ExperienceTab = 'assigned' | 'ended';

const EXPERIENCE_LIST_BATCH_SIZE = 20;

export interface AssignedProps {
  classId?: number;
  className?: string;
  schoolId?: number;
  subscriptionId?: number;
  userId: number;
  isTeacher: boolean;
  onNavigateToExperience?: (params: { experienceId: number }) => void;
  onEndNowExperience?: (params: { experienceId: number }) => void;
  onReactivateExperience?: (params: { experienceId: number }) => void;
  onAddExperience?: () => void;
  onInited: (value?: boolean) => void;
}

interface ActiveExperienceCardData {
  id: number;
  name: string;
  description?: string;
  classNameText?: string;
  imageUrl?: string;
  studentsStarted: number;
  studentsInvited: number;
  startDate: Date;
  endDate: Date;
  isGuided?: boolean;
  hasGroups?: boolean;
  readingGroupId?: number;
  templateId?: number;
  gradeId?: number;
  standardSetId?: number;
  standardSetLabel?: string;
  standards?: StandardsProps['standards'];
  canReactivate?: boolean;
  quizScore?: number;
  quizResponses?: number;
  teacherUserId?: number;
  invitedStudentIds?: number[];
}

interface SubscriptionExperienceFiltersResponse {
  schools?: Array<{ school_id: number; school_name: string }>;
  teachers?: SubscriptionTeacherFilter[];
  collections?: Array<{ top_collection_uuid: string; name: string }>;
}

interface SubscriptionTeacherFilter {
  teacher_user_id: number;
  teacher_first_name: string;
  teacher_last_name: string;
}

function getPrimaryTeacherName(experienceData: Record<string, unknown>): string {
  const teachers = Array.isArray(experienceData.teachers)
    ? experienceData.teachers as Array<Record<string, unknown>>
    : [];
  const primaryTeacher = teachers.find((teacher) => !!teacher.primary);
  if (!primaryTeacher) {
    return '';
  }

  const firstName = typeof primaryTeacher.first_name === 'string' ? primaryTeacher.first_name : '';
  const lastName = typeof primaryTeacher.last_name === 'string' ? primaryTeacher.last_name : '';
  if (!firstName && !lastName) {
    return '';
  }

  return `${lastName} ${firstName}, `;
}

function isCompleteStandardWithBreakouts(value: unknown): value is StandardWithBreakouts {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const standardWithBreakouts = value as {
    expectation?: Record<string, unknown>;
    breakouts?: unknown;
  };
  const expectation = standardWithBreakouts.expectation;
  const breakouts = standardWithBreakouts.breakouts;

  return !!expectation
    && typeof expectation.id === 'number'
    && typeof expectation.expectation_code === 'string'
    && typeof expectation.expectation_label === 'string'
    && Array.isArray(breakouts)
    && breakouts.every((breakout) => {
      if (!breakout || typeof breakout !== 'object') {
        return false;
      }
      const breakoutData = breakout as Record<string, unknown>;
      return typeof breakoutData.expectation_id === 'number'
        && breakoutData.expectation_id === expectation.id
        && typeof breakoutData.breakout_code === 'string'
        && typeof breakoutData.breakout_label === 'string'
        && typeof breakoutData.breakout_order === 'number';
    });
}

function mapExperiencesToCards(
  experiences: unknown[],
  hasSubscriptionContext: boolean = false
): ActiveExperienceCardData[] {
  return experiences.map((experience) => {
    const experienceData = (experience ?? {}) as Record<string, unknown>;
    const id = toRequiredNumber(experienceData.id, 'experience.id');
    const name = (experienceData.name as string | undefined) || 'Untitled Experience';
    const description = experienceData.description as string | undefined;
    const className = experienceData.class_name as string | undefined;
    const classNameText = hasSubscriptionContext
      ? `${getPrimaryTeacherName(experienceData)}${className || ''}`.trim()
      : className;
    const imageUrl = experienceData.image_filename as string | undefined;
    const studentsStarted = toOptionalNumber(experienceData.joined_students) ?? 0;
    const studentsInvited = toOptionalNumber(experienceData.invited_students) ?? 0;
    const startDate = toRequiredDate(experienceData.starts_at, 'experience.starts_at');
    const endDate = toRequiredDate(experienceData.ends_at, 'experience.ends_at');
    const smallGroups = toOptionalNumber(experienceData.small_groups);
    const hasGroups = smallGroups !== undefined ? smallGroups > 0 : !!experienceData.small_groups;
    const readingGroupId = toOptionalNumber(experienceData.reading_group_id);
    const isGuided = !!experienceData.is_guided;
    const templateId = toOptionalNumber(experienceData.template_id);
    const gradeId = toOptionalNumber(experienceData.grade_id);
    const standardSetId = toOptionalNumber(experienceData.standard_set_id);
    const standardSetLabel = experienceData.standard_set_label as string | undefined;
    const rawStandards = experienceData.standards;
    const standards = Array.isArray(rawStandards)
      && rawStandards.length > 0
      && rawStandards.every(isCompleteStandardWithBreakouts)
      ? rawStandards
      : undefined;
    const canReactivate = toOptionalNumber(experienceData.canReactivate) === 1;
    const quiz = (experienceData.quiz ?? {}) as Record<string, unknown>;
    const quizScore = toOptionalNumber(quiz.score);
    const quizResponses = toOptionalNumber(quiz.responses);
    const teacher = (experienceData.teacher ?? {}) as Record<string, unknown>;
    const teacherUserId = toOptionalNumber(teacher.id) ?? toOptionalNumber(experienceData.teacher_user_id);
    const rawInvitedStudentIds = Array.isArray(experienceData.invited_student_ids)
      ? experienceData.invited_student_ids
      : [];
    const invitedStudentIds = rawInvitedStudentIds
      .map((studentId, studentIndex) => toRequiredNumber(
        studentId,
        `experience.invited_student_ids[${studentIndex}]`
      ));

    return {
      id,
      name,
      description,
      classNameText,
      imageUrl,
      studentsStarted,
      studentsInvited,
      startDate,
      endDate,
      isGuided,
      hasGroups,
      readingGroupId,
      templateId,
      gradeId,
      standardSetId,
      standardSetLabel,
      standards,
      canReactivate,
      quizScore,
      quizResponses,
      teacherUserId,
      invitedStudentIds,
    };
  });
}

function mapTeacherToOption(teacher: SubscriptionTeacherFilter): SelectOption<number> {
  return {
    id: teacher.teacher_user_id,
    name: `${teacher.teacher_first_name} ${teacher.teacher_last_name}`.trim(),
  };
}

function shouldShowLoadMore(experienceCount: number, additionalLimit: number): boolean {
  return experienceCount >= EXPERIENCE_LIST_BATCH_SIZE + additionalLimit;
}

export const Assigned = ({
  classId,
  className,
  schoolId,
  subscriptionId,
  userId,
  isTeacher,
  onNavigateToExperience,
  onEndNowExperience,
  onReactivateExperience,
  onAddExperience,
  onInited
}: AssignedProps) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<ExperienceTab>('assigned');
  const [activeExperiences, setActiveExperiences] = useState<ActiveExperienceCardData[]>([]);
  const [scheduledExperiences, setScheduledExperiences] = useState<ActiveExperienceCardData[]>([]);
  const [endedExperiences, setEndedExperiences] = useState<ActiveExperienceCardData[]>([]);
  const [hasSettledExperienceRequests, setHasSettledExperienceRequests] = useState(false);
  const [confirmingEndNowExperienceId, setConfirmingEndNowExperienceId] = useState<number | null>(null);
  const [confirmingReactivateExperienceId, setConfirmingReactivateExperienceId] = useState<number | null>(null);
  const [schoolOptions, setSchoolOptions] = useState<SelectOption<number>[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<SelectOption<number>[]>([]);
  const [collectionOptions, setCollectionOptions] = useState<SelectOption<string>[]>([]);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<number[]>([ALL_OPTION_NUMBER_ID]);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<number[]>([ALL_OPTION_NUMBER_ID]);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([ALL_OPTION_ID]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [endedExperienceNotificationCount, setEndedExperienceNotificationCount] = useState(0);
  const [hasMoreActiveExperiences, setHasMoreActiveExperiences] = useState(false);
  const [hasMoreEndedExperiences, setHasMoreEndedExperiences] = useState(false);
  const [isLoadingMoreActiveExperiences, setIsLoadingMoreActiveExperiences] = useState(false);
  const [isLoadingMoreEndedExperiences, setIsLoadingMoreEndedExperiences] = useState(false);
  const hasLoadedSubscriptionFiltersRef = useRef(false);
  const filterRequestSeqRef = useRef(0);
  const activeAdditionalLimitRef = useRef(0);
  const endedAdditionalLimitRef = useRef(0);
  // Pagination advances the list counters independently of full-load completion.
  const experienceLoadSeqRef = useRef(0);
  // Separate generations prevent stale reloads without making Active and Ended cancel each other.
  const activeRequestSeqRef = useRef(0);
  const endedRequestSeqRef = useRef(0);
  const normalizedClassId = toOptionalNumber(classId);
  const normalizedSchoolId = toOptionalNumber(schoolId);
  const normalizedSubscriptionId = toOptionalNumber(subscriptionId);
  const normalizedUserId = toRequiredNumber(userId, 'userId');
  const hasClassContext = normalizedClassId !== undefined && normalizedClassId > 0;
  const hasSchoolContext = normalizedSchoolId !== undefined && normalizedSchoolId > 0;
  const hasSubscriptionContext = normalizedSubscriptionId !== undefined && normalizedSubscriptionId > 0;
  const canManageExperienceStatus = isTeacher === true;
  const isStudent = isTeacher !== true;
  const assignExperienceText = hasClassContext
    ? (className ? `Assign an experience to class ${className}` : '')
    : 'Assign an experience to your classes';

  useEffect(() => {
    hasLoadedSubscriptionFiltersRef.current = false;
  }, [normalizedSubscriptionId]);

  const applyAllSelectionRule = <TId extends string | number>(selectedIds: TId[], allOptionId: TId): TId[] => {
    if (selectedIds.length === 0) {
      return [allOptionId];
    }
    if (selectedIds.includes(allOptionId) && selectedIds.length > 1) {
      return [allOptionId];
    }
    return selectedIds;
  };

  const subscriptionExperienceFilter = useMemo<AssignedExperienceFilter | undefined>(() => {
    if (!hasSubscriptionContext) {
      return undefined;
    }

    const schools = selectedSchoolIds.includes(ALL_OPTION_NUMBER_ID) ? [] : selectedSchoolIds;
    const normalizedSchools = hasSchoolContext
      ? [normalizedSchoolId]
      : schools;
    const teachers = selectedTeacherIds.includes(ALL_OPTION_NUMBER_ID) ? [] : selectedTeacherIds;
    const courses = selectedCollectionIds.includes(ALL_OPTION_ID) ? [] : selectedCollectionIds;

    if (normalizedSchools.length === 0 && teachers.length === 0 && courses.length === 0) {
      return undefined;
    }

    return { schools: normalizedSchools, teachers, courses };
  }, [
    hasSubscriptionContext,
    hasSchoolContext,
    normalizedSchoolId,
    selectedSchoolIds,
    selectedTeacherIds,
    selectedCollectionIds
  ]);

  const clearEndedExperienceNotifications = useCallback(async () => {
    try {
      await EndedExperienceNotificationService.removeEndedExperiences(normalizedUserId);
      setEndedExperienceNotificationCount(0);
    } catch {
      // No-op: keep existing notification count if clear request fails.
    }
  }, [normalizedUserId]);

  const handleTabChange = (_event: React.SyntheticEvent, tab: ExperienceTab) => {
    setActiveTab(tab);
    if (tab === 'ended') {
      void clearEndedExperienceNotifications();
    }
  };

  const getExperiencesByStatus = useCallback(
    (status: AssignedExperienceStatus, additionalLimit: number = 0) => {
      if (hasSubscriptionContext) {
        return AssignedService.getSubscriptionExperiencesByStatus(
          normalizedSubscriptionId,
          status,
          subscriptionExperienceFilter,
          additionalLimit
        );
      }
      if (hasClassContext) {
        return AssignedService.getClassExperiencesByStatus(normalizedClassId, status, additionalLimit);
      }
      return AssignedService.getAllExperiencesByStatus(status, additionalLimit);
    },
    [
      hasSubscriptionContext,
      normalizedSubscriptionId,
      subscriptionExperienceFilter,
      hasClassContext,
      normalizedClassId
    ]
  );

  const loadBackgroundExperiences = useCallback(
    async (shouldUpdate: () => boolean) => {
      const shouldLoadSubscriptionFilters = hasSubscriptionContext && !hasLoadedSubscriptionFiltersRef.current;
      const endedAdditionalLimit = endedAdditionalLimitRef.current;
      const backgroundRequests: Promise<unknown>[] = [
        getExperiencesByStatus('pending'),
        getExperiencesByStatus('past', endedAdditionalLimit),
      ];
      if (shouldLoadSubscriptionFilters) {
        backgroundRequests.push(AssignedService.getSubscriptionExperienceFilters(normalizedSubscriptionId));
      }

      const backgroundResults = await Promise.allSettled(backgroundRequests);

      if (!shouldUpdate()) {
        return;
      }

      const [scheduledResult, endedResult, subscriptionFiltersResult] = backgroundResults;
      const endedExperienceValues = endedResult.status === 'fulfilled' && Array.isArray(endedResult.value)
        ? endedResult.value
        : [];

      setScheduledExperiences(
        scheduledResult.status === 'fulfilled'
          ? mapExperiencesToCards(
            Array.isArray(scheduledResult.value) ? scheduledResult.value : [],
            hasSubscriptionContext
          )
          : []
      );
      setEndedExperiences(
        mapExperiencesToCards(endedExperienceValues, hasSubscriptionContext)
      );
      setHasMoreEndedExperiences(
        shouldShowLoadMore(endedExperienceValues.length, endedAdditionalLimit)
      );
      setLastUpdatedAt(new Date());
      if (endedExperienceValues.length > 0) {
        void EndedExperienceNotificationService.getEndedExperiences(normalizedUserId)
          .then((notifications) => {
            if (shouldUpdate()) {
              setEndedExperienceNotificationCount(Array.isArray(notifications) ? notifications.length : 0);
            }
          })
          .catch(() => {
            if (shouldUpdate()) {
              setEndedExperienceNotificationCount(0);
            }
          });
      } else {
        setEndedExperienceNotificationCount(0);
      }

      if (shouldLoadSubscriptionFilters && subscriptionFiltersResult?.status === 'fulfilled') {
        const filtersResponse = subscriptionFiltersResult.value as SubscriptionExperienceFiltersResponse;
        const mappedSchoolOptions: SelectOption<number>[] = (filtersResponse.schools || []).map((school) => ({
          id: school.school_id,
          name: school.school_name,
        }));
        const mappedTeacherOptions: SelectOption<number>[] = (filtersResponse.teachers || []).map(mapTeacherToOption);
        const mappedCollectionOptions: SelectOption<string>[] = (filtersResponse.collections || []).map((collection) => ({
          id: collection.top_collection_uuid,
          name: collection.name,
        }));

        setSchoolOptions(mappedSchoolOptions);
        setTeacherOptions(mappedTeacherOptions);
        setCollectionOptions(mappedCollectionOptions);
        setSelectedSchoolIds([ALL_OPTION_NUMBER_ID]);
        setSelectedTeacherIds([ALL_OPTION_NUMBER_ID]);
        setSelectedCollectionIds([ALL_OPTION_ID]);
        hasLoadedSubscriptionFiltersRef.current = true;
      } else if (shouldLoadSubscriptionFilters) {
        setSchoolOptions([]);
        setTeacherOptions([]);
        setCollectionOptions([]);
        setSelectedSchoolIds([ALL_OPTION_NUMBER_ID]);
        setSelectedTeacherIds([ALL_OPTION_NUMBER_ID]);
        setSelectedCollectionIds([ALL_OPTION_ID]);
        hasLoadedSubscriptionFiltersRef.current = true;
      }
    },
    [getExperiencesByStatus, hasSubscriptionContext, normalizedSubscriptionId, normalizedUserId]
  );

  const loadActiveExperiences = useCallback(
    async (shouldUpdate: () => boolean) => {
      const experienceLoadSeq = ++experienceLoadSeqRef.current;
      const activeRequestSeq = ++activeRequestSeqRef.current;
      const endedRequestSeq = ++endedRequestSeqRef.current;
      const shouldUpdateActive = () =>
        shouldUpdate() && activeRequestSeq === activeRequestSeqRef.current;
      const shouldUpdateEnded = () =>
        shouldUpdate() && endedRequestSeq === endedRequestSeqRef.current;

      if (shouldUpdate()) {
        setHasSettledExperienceRequests(false);
        setIsLoadingMoreActiveExperiences(false);
        setIsLoadingMoreEndedExperiences(false);
      }

      try {
        const activeAdditionalLimit = activeAdditionalLimitRef.current;
        const experiences = await getExperiencesByStatus('active', activeAdditionalLimit);

        if (shouldUpdateActive()) {
          setActiveExperiences(mapExperiencesToCards(experiences, hasSubscriptionContext));
          setHasMoreActiveExperiences(
            shouldShowLoadMore(experiences.length, activeAdditionalLimit)
          );
        }
        if (shouldUpdateEnded()) {
          await loadBackgroundExperiences(shouldUpdateEnded);
        }
      } catch {
        if (shouldUpdateActive()) {
          setActiveExperiences([]);
          setHasMoreActiveExperiences(false);
        }
        if (shouldUpdateEnded()) {
          setScheduledExperiences([]);
          setEndedExperiences([]);
          setHasMoreEndedExperiences(false);
        }
      } finally {
        if (shouldUpdate() && experienceLoadSeq === experienceLoadSeqRef.current) {
          setHasSettledExperienceRequests(true);
          onInited(true);
        }
      }
    },
    [getExperiencesByStatus, hasSubscriptionContext, loadBackgroundExperiences, onInited]
  );

  useEffect(() => {
    let isSubscribed = true;
    const shouldUpdate = () => isSubscribed;
    activeAdditionalLimitRef.current = 0;
    endedAdditionalLimitRef.current = 0;
    setHasMoreActiveExperiences(false);
    setHasMoreEndedExperiences(false);
    void loadActiveExperiences(shouldUpdate);

    return () => {
      isSubscribed = false;
      activeRequestSeqRef.current += 1;
      endedRequestSeqRef.current += 1;
    };
  }, [loadActiveExperiences]);

  useAssignedRealtime<ActiveExperienceCardData>({
    loadActiveExperiences,
    scheduledExperiences,
    endedExperiences,
    setActiveExperiences,
    setScheduledExperiences,
    setEndedExperiences,
    mapSingleExperienceToCard: (experience) => mapSingleExperienceToCard(
      experience,
      (experiences) => mapExperiencesToCards(experiences, hasSubscriptionContext)
    ),
    insertExperienceByStartDate,
  });

  const handleEndNowClick = useCallback(({ experienceId }: { experienceId: number }) => {
    setConfirmingEndNowExperienceId(experienceId);
    setConfirmingReactivateExperienceId(null);
  }, []);

  const handleCancelEndNow = useCallback(() => {
    setConfirmingEndNowExperienceId(null);
  }, []);

  const handleConfirmEndNow = useCallback(({ experienceId }: { experienceId: number }) => {
    if (!canManageExperienceStatus) {
      setConfirmingEndNowExperienceId(null);
      return;
    }
    onEndNowExperience?.({ experienceId });
    setConfirmingEndNowExperienceId(null);
  }, [canManageExperienceStatus, onEndNowExperience]);

  const handleExperienceClick = useCallback(({ experienceId }: { experienceId: number }) => {
    onNavigateToExperience?.({ experienceId });
  }, [onNavigateToExperience]);

  const handleReactivateClick = useCallback(({ experienceId }: { experienceId: number }) => {
    setConfirmingReactivateExperienceId(experienceId);
    setConfirmingEndNowExperienceId(null);
  }, []);

  const handleCancelReactivate = useCallback(() => {
    setConfirmingReactivateExperienceId(null);
  }, []);

  const handleConfirmReactivate = useCallback(({ experienceId }: { experienceId: number }) => {
    if (!canManageExperienceStatus) {
      setConfirmingReactivateExperienceId(null);
      return;
    }
    onReactivateExperience?.({ experienceId });
    setConfirmingReactivateExperienceId(null);
  }, [canManageExperienceStatus, onReactivateExperience]);

  const fetchSubscriptionFilterOptions = useCallback((
    schoolIds: number[],
    teacherIds: number[]
  ) => {
    const seq = ++filterRequestSeqRef.current;
    return AssignedService.getSubscriptionExperienceFilters(
      normalizedSubscriptionId,
      { school_ids: schoolIds, teachers: teacherIds, collection_uuids: [] }
    )
      .then((filtersResponse) => {
        if (seq !== filterRequestSeqRef.current) {
          return;
        }
        const response = filtersResponse as SubscriptionExperienceFiltersResponse;
        setTeacherOptions((response.teachers || []).map(mapTeacherToOption));
        setCollectionOptions((response.collections || []).map((collection) => ({
          id: collection.top_collection_uuid,
          name: collection.name,
        })));
      })
      .catch(() => {
        if (seq !== filterRequestSeqRef.current) {
          return;
        }
        setTeacherOptions([]);
        setCollectionOptions([]);
      });
  }, [normalizedSubscriptionId]);

  const handleSchoolFilterClose = useCallback((selectedIds: number[]) => {
    if (!hasSubscriptionContext) {
      return;
    }
    const normalizedSelectedSchoolIds = applyAllSelectionRule(selectedIds, ALL_OPTION_NUMBER_ID);
    const selectedSchoolIdsForFilter = normalizedSelectedSchoolIds.includes(ALL_OPTION_NUMBER_ID)
      ? []
      : normalizedSelectedSchoolIds;

    setSelectedSchoolIds(normalizedSelectedSchoolIds);
    setSelectedTeacherIds([ALL_OPTION_NUMBER_ID]);
    setSelectedCollectionIds([ALL_OPTION_ID]);
    void fetchSubscriptionFilterOptions(selectedSchoolIdsForFilter, []);
  }, [fetchSubscriptionFilterOptions, hasSubscriptionContext]);

  const handleTeacherFilterClose = useCallback((selectedIds: number[]) => {
    if (!hasSubscriptionContext) {
      return;
    }
    const normalizedSelectedTeacherIds = applyAllSelectionRule(selectedIds, ALL_OPTION_NUMBER_ID);
    const selectedTeachersForFilter = normalizedSelectedTeacherIds.includes(ALL_OPTION_NUMBER_ID)
      ? []
      : normalizedSelectedTeacherIds;
    const selectedSchoolsForFilter = hasSchoolContext
      ? [normalizedSchoolId]
      : (selectedSchoolIds.includes(ALL_OPTION_NUMBER_ID) ? [] : selectedSchoolIds);

    setSelectedTeacherIds(normalizedSelectedTeacherIds);
    setSelectedCollectionIds([ALL_OPTION_ID]);
    void fetchSubscriptionFilterOptions(selectedSchoolsForFilter, selectedTeachersForFilter);
  }, [
    fetchSubscriptionFilterOptions,
    hasSchoolContext,
    hasSubscriptionContext,
    normalizedSchoolId,
    selectedSchoolIds
  ]);

  const handleCollectionFilterClose = useCallback((selectedIds: string[]) => {
    setSelectedCollectionIds(applyAllSelectionRule(selectedIds, ALL_OPTION_ID));
  }, []);

  const loadMoreExperiences = useCallback(async (
    status: Extract<AssignedExperienceStatus, 'active' | 'past'>,
    currentAdditionalLimit: number
  ) => {
    const nextAdditionalLimit = currentAdditionalLimit + EXPERIENCE_LIST_BATCH_SIZE;
    const experiences = await getExperiencesByStatus(status, nextAdditionalLimit);

    return {
      additionalLimit: nextAdditionalLimit,
      experiences: mapExperiencesToCards(experiences, hasSubscriptionContext),
      hasMore: shouldShowLoadMore(experiences.length, nextAdditionalLimit),
    };
  }, [getExperiencesByStatus, hasSubscriptionContext]);

  const handleLoadMoreExperiences = useCallback(async (
    status: Extract<AssignedExperienceStatus, 'active' | 'past'>
  ) => {
    const isActive = status === 'active';
    const isLoading = isActive
      ? isLoadingMoreActiveExperiences
      : isLoadingMoreEndedExperiences;
    if (isLoading) {
      return;
    }

    const additionalLimitRef = isActive
      ? activeAdditionalLimitRef
      : endedAdditionalLimitRef;
    const setExperiences = isActive
      ? setActiveExperiences
      : setEndedExperiences;
    const setHasMore = isActive
      ? setHasMoreActiveExperiences
      : setHasMoreEndedExperiences;
    const setIsLoading = isActive
      ? setIsLoadingMoreActiveExperiences
      : setIsLoadingMoreEndedExperiences;
    const requestSeqRef = isActive
      ? activeRequestSeqRef
      : endedRequestSeqRef;
    const requestSeq = ++requestSeqRef.current;

    setIsLoading(true);
    try {
      const result = await loadMoreExperiences(status, additionalLimitRef.current);
      if (requestSeq !== requestSeqRef.current) {
        return;
      }
      additionalLimitRef.current = result.additionalLimit;
      setExperiences(result.experiences);
      setHasMore(result.hasMore);
    } catch {
      // Keep the current list and button available so the request can be retried.
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    isLoadingMoreActiveExperiences,
    isLoadingMoreEndedExperiences,
    loadMoreExperiences
  ]);

  const handleRefreshExperiences = useCallback(() => {
    const shouldUpdate = () => true;
    void loadActiveExperiences(shouldUpdate);
  }, [loadActiveExperiences]);

  const handleAddExperience = useCallback(() => {
    onAddExperience?.();
  }, [onAddExperience]);

  const totalExperiences = activeExperiences.length;

  const totalStudents = useMemo(() => {
    const uniqueStudentIds = new Set<number>();
    activeExperiences.forEach((experience) => {
      (experience.invitedStudentIds || []).forEach((studentId) => {
        uniqueStudentIds.add(studentId);
      });
    });
    return uniqueStudentIds.size;
  }, [activeExperiences]);

  const totalTeachers = useMemo(() => {
    const uniqueTeacherIds = new Set(
      activeExperiences
        .map((experience) => experience.teacherUserId)
        .filter((teacherUserId): teacherUserId is number => typeof teacherUserId === 'number')
    );
    return uniqueTeacherIds.size;
  }, [activeExperiences]);
  
  const summaryItems = useMemo(
    () => [
      { value: totalTeachers, label: 'Teachers' },
      { value: totalExperiences, label: 'Experiences' },
      { value: totalStudents, label: 'Students' },
    ],
    [totalTeachers, totalExperiences, totalStudents]
  );
  // Preserve pending status so scheduled cards remain closed to students after combining the lists.
  const assignedExperiences = [
    ...activeExperiences.map((experience) => ({ ...experience, isScheduled: false })),
    ...scheduledExperiences.map((experience) => ({ ...experience, isScheduled: true })),
  ];
  const lastUpdatedText = lastUpdatedAt ? formatLastUpdated(lastUpdatedAt) : '--';
  const filters = hasSubscriptionContext ? (
    <Box sx={assignedStyles.summaryFiltersRow(theme)}>
      {!hasSchoolContext && (
        <Box sx={assignedStyles.filterSelectContainer()}>
          <SelectComponent
            label="Schools"
            options={schoolOptions}
            multiple={true}
            allOptionLabel="Schools"
            allOptionId={ALL_OPTION_NUMBER_ID}
            value={selectedSchoolIds}
            onCloseChange={handleSchoolFilterClose}
          />
        </Box>
      )}
      <Box sx={assignedStyles.filterSelectContainer()}>
        <SelectComponent
          label="Teachers"
          options={teacherOptions}
          multiple={true}
          allOptionLabel="Teachers"
          allOptionId={ALL_OPTION_NUMBER_ID}
          value={selectedTeacherIds}
          onCloseChange={handleTeacherFilterClose}
        />
      </Box>
      <Box sx={assignedStyles.filterSelectContainer()}>
        <SelectComponent
          label="Collections"
          options={collectionOptions}
          multiple={true}
          allOptionLabel="Courses"
          allOptionId={ALL_OPTION_ID}
          value={selectedCollectionIds}
          onCloseChange={handleCollectionFilterClose}
        />
      </Box>
    </Box>
  ) : null;

  return (
    <Box component="section" aria-label="Experiences list" sx={assignedStyles.container(theme)}>
      {hasSubscriptionContext && (
        <Box sx={assignedStyles.summaryAndFiltersRow(theme)}>
          <Box sx={assignedStyles.summaryHeaderPlacement()}>
            <SummaryHeader items={summaryItems} separators={['teaching', 'reaching']} />
          </Box>
          {filters}
        </Box>
      )}
      <Box sx={assignedStyles.tabsRow(theme, hasSubscriptionContext)}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Experience status"
          sx={assignedStyles.tabs(hasSubscriptionContext)}
        >
          <Tab
            value="assigned"
            label="Assigned Experiences"
            id="experiences-tab-assigned"
            aria-controls="experiences-panel-assigned"
            sx={assignedStyles.tab(theme)}
          />
          <Tab
            value="ended"
            label={endedExperienceNotificationCount > 0 ? (
              <Badge
                badgeContent={endedExperienceNotificationCount}
                color="error"
                sx={assignedStyles.endedNotificationBadge()}
              >
                Ended Experiences
              </Badge>
            ) : 'Ended Experiences'}
            id="experiences-tab-ended"
            aria-controls="experiences-panel-ended"
            sx={assignedStyles.tab(theme)}
          />
        </Tabs>
        {hasSubscriptionContext && (
          <Box sx={assignedStyles.lastUpdatedRow(theme)}>
            <Typography sx={assignedStyles.lastUpdatedText(theme)}>
              Last updated: {lastUpdatedText}
            </Typography>
            <IconButton
              aria-label="Refresh experiences"
              onClick={handleRefreshExperiences}
              sx={assignedStyles.refreshButton(theme)}
            >
              <Box
                component="img"
                src={RefreshIcon}
                alt=""
                aria-hidden={true}
                sx={assignedStyles.refreshIcon()}
              />
            </IconButton>
          </Box>
        )}
        {!hasSubscriptionContext && isTeacher && (
          <IconButton
            id="add_experience_button"
            aria-label="Assign Experience"
            title="Assign Experience"
            data-qa-role="btn#addExperience"
            onClick={handleAddExperience}
            sx={assignedStyles.addExperienceButton(theme)}
          >
            <Box
              component="img"
              src={AddExperienceIcon}
              alt=""
              aria-hidden={true}
              sx={assignedStyles.addExperienceIcon()}
            />
          </IconButton>
        )}
      </Box>
      {!hasSubscriptionContext && isTeacher && assignExperienceText && (
        <Typography component="p" sx={assignedStyles.assignExperienceText(theme)}>
          {assignExperienceText}
        </Typography>
      )}
      {(['assigned', 'ended'] as const).map((tab) => (
        <Box
          key={tab}
          role="tabpanel"
          id={`experiences-panel-${tab}`}
          aria-labelledby={`experiences-tab-${tab}`}
          hidden={activeTab !== tab}
          sx={assignedStyles.tabPanel()}
        >
          {tab === 'assigned' && (
            <Box sx={assignedStyles.experiencesList(theme)}>
              {hasSettledExperienceRequests && assignedExperiences.length === 0 ? (
                <Box sx={assignedStyles.emptyAssignedState()}>
                  <Box
                    component="img"
                    src={EmptyCardImage}
                    alt="No assigned experiences"
                    sx={assignedStyles.emptyAssignedStateImage()}
                  />
                </Box>
              ) : (
                assignedExperiences.map((experience) => (
                  <ExperienceCard
                    key={experience.id}
                    experienceId={experience.id}
                    name={experience.name}
                    description={experience.description}
                    classNameText={experience.classNameText}
                    imageUrl={experience.imageUrl}
                    studentsStarted={experience.studentsStarted}
                    studentsInvited={experience.studentsInvited}
                    startDate={experience.startDate}
                    endDate={experience.endDate}
                    isGuided={experience.isGuided}
                    hasGroups={experience.hasGroups}
                    readingGroupId={experience.readingGroupId}
                    templateId={experience.templateId}
                    gradeId={experience.gradeId}
                    standardSetId={experience.standardSetId}
                    standardSetLabel={experience.standardSetLabel}
                    standards={experience.standards}
                    isStudent={isStudent}
                    isAssignedExperience={true}
                    isScheduledExperience={experience.isScheduled}
                    canOpenExperience={!isStudent || !experience.isScheduled}
                    isEndNowConfirmOpen={confirmingEndNowExperienceId === experience.id}
                    onExperienceClick={handleExperienceClick}
                    onEndNowClick={canManageExperienceStatus ? handleEndNowClick : undefined}
                    onConfirmEndNow={canManageExperienceStatus ? handleConfirmEndNow : undefined}
                    onCancelEndNow={canManageExperienceStatus ? handleCancelEndNow : undefined}
                  />
                ))
              )}
              {isTeacher && hasMoreActiveExperiences && (
                <LoadMoreButton
                  ariaLabel="Show more assigned experiences"
                  isLoading={isLoadingMoreActiveExperiences}
                  onClick={() => void handleLoadMoreExperiences('active')}
                />
              )}
            </Box>
          )}
          {tab === 'ended' && (
            <Box sx={assignedStyles.experiencesList(theme)}>
              {endedExperiences.map((experience) => (
                <ExperienceCard
                  key={experience.id}
                  experienceId={experience.id}
                  name={experience.name}
                  description={experience.description}
                  classNameText={experience.classNameText}
                  imageUrl={experience.imageUrl}
                  studentsStarted={experience.studentsStarted}
                  studentsInvited={experience.studentsInvited}
                  startDate={experience.startDate}
                  endDate={experience.endDate}
                  isGuided={experience.isGuided}
                  hasGroups={experience.hasGroups}
                  readingGroupId={experience.readingGroupId}
                  templateId={experience.templateId}
                  gradeId={experience.gradeId}
                  standardSetId={experience.standardSetId}
                  standardSetLabel={experience.standardSetLabel}
                  standards={experience.standards}
                  isStudent={isStudent}
                  isEndedExperience={true}
                  canReactivate={experience.canReactivate}
                  quizScore={experience.quizScore}
                  quizResponses={experience.quizResponses}
                  onExperienceClick={handleExperienceClick}
                  onReactivateClick={canManageExperienceStatus ? handleReactivateClick : undefined}
                  isReactivateConfirmOpen={confirmingReactivateExperienceId === experience.id}
                  onConfirmReactivate={canManageExperienceStatus ? handleConfirmReactivate : undefined}
                  onCancelReactivate={canManageExperienceStatus ? handleCancelReactivate : undefined}
                />
              ))}
              {isTeacher && hasMoreEndedExperiences && (
                <LoadMoreButton
                  ariaLabel="Show more ended experiences"
                  isLoading={isLoadingMoreEndedExperiences}
                  onClick={() => void handleLoadMoreExperiences('past')}
                />
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default Assigned;
